// src/firebaseFunctions.ts
//import firebase from "./firebase-config"; // Firebase 초기 설정을 가져옵니다.
//import "firebase/functions"; // Firebase Functions를 가져옵니다.
import {
  AdminMembersResponse,
  BillingTransactionsResponse,
  CancelDodoSubscriptionRequestParam,
  CancelDodoSubscriptionResponse,
  DodoCheckoutRequestParam,
  DodoCheckoutResponse,
  NextExpiringCRDResponse,
  UserDataResponse,
  UserDataRequestParam,
  VideoTask,
  TaskStatus,
} from "../types"; // 타입 정의를 가져옵니다.
import { getApp } from "firebase/app";
import { getFunctions, httpsCallable } from "firebase/functions";
import { BlogPost } from "../blogContent";
// import { app } from "./firebase-config"; // Firebase 초기 설정을 가져옵니다.

// Firebase Function 호출 함수
// getTasks 함수가 Promise를 반환하도록 수정
export const getTasks = async (userId: string): Promise<VideoTask[]> => {
  const functions = getFunctions(getApp(), "asia-northeast3");
  const getTasksFn = httpsCallable(functions, "getTasksOnCall");

  try {
    const response = await getTasksFn({ userId });
    const tasks = response.data as VideoTask[];
    return tasks;
  } catch (error: any) {
    console.error("Error calling getTasks function:", error.message);
    throw error;
  }
};

export const updateTaskOrder = async (userId: string, docIds: string[]) => {
  const functions = getFunctions(getApp(), "asia-northeast3");
  const updateTaskOrderFn = httpsCallable(functions, "updateTaskOrder");

  try {
    const response = await updateTaskOrderFn({ userId, docIds });
    return response.data; // 작업 순서 업데이트 성공 메시지
  } catch (error: any) {
    console.error("Error calling updateTaskOrder function:", error.message);
    throw error;
  }
};

export const addTask = async (userId: string, taskDetails: VideoTask) => {
  const functions = getFunctions(getApp(), "asia-northeast3");
  const addTaskFn = httpsCallable(functions, "addTask");
  try {
    const response = await addTaskFn({ userId, taskDetails });
    return response.data; // 작업 추가 성공 메시지
  } catch (error: any) {
    console.error("Error adding task:", error.message);
    throw error;
  }
};

export const updateChannels = async (userId: string, channels: string[]) => {
  const functions = getFunctions(getApp(), "asia-northeast3");
  const updateChannelsFn = httpsCallable(functions, "updateChannels");
  try {
    const response = await updateChannelsFn({ userId, channels });
    return response.data; // 작업 업데이트 성공 메시지
  } catch (error: any) {
    console.error("Error updating task:", error.message);
    throw error;
  }
};

export const updateTask = async (
  userId: string,
  taskId: string,
  taskDetails: VideoTask
) => {
  const functions = getFunctions(getApp(), "asia-northeast3");
  const updateTaskFn = httpsCallable(functions, "updateTaskOnCall");
  try {
    const response = await updateTaskFn({ userId, taskId, taskDetails });
    return response.data; // 작업 업데이트 성공 메시지
  } catch (error: any) {
    console.error("Error updating task:", error.message);
    throw error;
  }
};

export const updateTaskStatus = async (
  userId: string,
  taskId: string,
  status: TaskStatus
) => {
  const functions = getFunctions(getApp(), "asia-northeast3");
  const updateTaskStatusFn = httpsCallable(functions, "updateTaskStatusOnCall");
  try {
    const response = await updateTaskStatusFn({ userId, taskId, status });
    return response.data; // 작업 상태 업데이트 성공 메시지
  } catch (error: any) {
    console.error("Error updating task status:", error.message);
    throw error;
  }
};

export const deleteTask = async (userId: string, taskId: string) => {
  const functions = getFunctions(getApp(), "asia-northeast3");
  const deleteTaskFn = httpsCallable(functions, "deleteTask");
  try {
    const response = await deleteTaskFn({ userId, taskId });
    return response.data; // 작업 삭제 성공 메시지
  } catch (error: any) {
    console.error("Error deleting task:", error.message);
    throw error;
  }
};

export const getUserData = async (
  uid: string,
  userId: string
): Promise<UserDataResponse> => {
  const functions = getFunctions(getApp(), "asia-northeast3");
  const getUserDataFn = httpsCallable(functions, "getAutoVidUserDataOnCall");
  try {
    const requestParam = {
      uid: uid,
      userId: userId,
      deleteAuthWhenNotJoined: true,
    } as UserDataRequestParam;
    const response = await getUserDataFn({
      userdata: requestParam,
    });
    return response.data as UserDataResponse; // 반환 타입을 명시적으로 캐스팅
  } catch (error: any) {
    if (error.code === "functions/unauthenticated") {
      throw new Error("unauthenticated user.");
    } else if (error.code === "functions/not-found") {
      throw new Error("At first, register as a member.");
    } else {
      console.error("Error calling getUserData function:", error.message);
      throw error;
    }
  }
};

export const withdrawMember = async (userId: string) => {
  const functions = getFunctions(getApp(), "asia-northeast3");
  const withdrawAutoVidMemberFn = httpsCallable(
    functions,
    "withdrawAutoVidMember"
  );
  try {
    // const requestParam = {
    //   uid: uid,
    //   userId: userId } as UserDataRequestParam;
    await withdrawAutoVidMemberFn({
      userId: userId,
    });
    return; // 반환 타입을 명시적으로 캐스팅
  } catch (error: any) {
    throw error;
  }
};

export const registerMember = async (
  userId: string
): Promise<UserDataResponse> => {
  const functions = getFunctions(getApp(), "asia-northeast3");
  const registerMemberFn = httpsCallable(
    functions,
    "registerAutoVidMemberOnCall"
  );
  try {
    const response = await registerMemberFn({
      userId: userId,
    });
    return response.data as UserDataResponse; // 반환 타입을 명시적으로 캐스팅
  } catch (error: any) {
    if (error.code === "functions/already-exists") {
      throw new Error("Already registered user.");
    } else {
      console.error("Error calling getUserData function:", error.message);
      throw error;
    }
  }
};

export const getDodoCheckoutUrl = async (
  userId: string,
  productCode: string,
  locale: string
): Promise<string> => {
  const functions = getFunctions(getApp(), "asia-northeast3");
  const getDodoCheckoutUrlFn = httpsCallable(
    functions,
    "getDodoCheckoutUrlOnCall"
  );
  try {
    const requestParam = {
      userId,
      productCode,
      locale,
    } as DodoCheckoutRequestParam;
    const response = await getDodoCheckoutUrlFn(requestParam);
    const data = response.data as DodoCheckoutResponse;
    if (!data.checkoutUrl) {
      throw new Error("Checkout URL is missing.");
    }
    return data.checkoutUrl;
  } catch (error: any) {
    if (error.code === "functions/failed-precondition") {
      throw new Error(
        error.message ||
          "You already have an active subscription or checkout is unavailable."
      );
    }
    if (error.code === "functions/permission-denied") {
      throw new Error(
        error.message ||
          "This account is not allowed to use checkout in test mode."
      );
    }
    throw error;
  }
};

export const cancelDodoSubscription = async (
  userId: string
): Promise<CancelDodoSubscriptionResponse> => {
  const functions = getFunctions(getApp(), "asia-northeast3");
  const cancelDodoSubscriptionFn = httpsCallable(
    functions,
    "cancelDodoSubscriptionOnCall"
  );
  try {
    const requestParam = {
      userId,
    } as CancelDodoSubscriptionRequestParam;
    const response = await cancelDodoSubscriptionFn(requestParam);
    return response.data as CancelDodoSubscriptionResponse;
  } catch (error: any) {
    if (error.code === "functions/failed-precondition") {
      throw new Error(
        error.message ||
          "No cancellable subscription was found or Dodo API is unavailable."
      );
    }
    throw error;
  }
};

export const getNextExpiringCRD = async (
  userId: string
): Promise<NextExpiringCRDResponse | null> => {
  const functions = getFunctions(getApp(), "asia-northeast3");
  const getNextExpiringCRDFn = httpsCallable(
    functions,
    "getNextExpiringCRDOnCall"
  );

  try {
    const response = await getNextExpiringCRDFn({ userId });
    return (response.data ?? null) as NextExpiringCRDResponse | null;
  } catch (error: any) {
    console.error("Error calling getNextExpiringCRD function:", error.message);
    throw error;
  }
};

export const getBillingTransactions = async (
  userId: string,
  limit = 10,
  cursor: string | null = null
): Promise<BillingTransactionsResponse> => {
  const functions = getFunctions(getApp(), "asia-northeast3");
  const getBillingTransactionsFn = httpsCallable(
    functions,
    "getBillingTransactionsOnCall"
  );

  try {
    const response = await getBillingTransactionsFn({ userId, limit, cursor });
    return response.data as BillingTransactionsResponse;
  } catch (error: any) {
    if (error.code === "functions/permission-denied") {
      throw new Error(error.message ?? "You can read only your own payment history.");
    }
    if (error.code === "functions/unauthenticated") {
      throw new Error(error.message ?? "Please sign in first.");
    }
    throw error;
  }
};

export const getAdminMembers = async (
  keyword = "",
  limit = 50,
  cursor: string | null = null
): Promise<AdminMembersResponse> => {
  const functions = getFunctions(getApp(), "asia-northeast3");
  const getAdminMembersFn = httpsCallable(functions, "getAdminMembersOnCall");

  try {
    const response = await getAdminMembersFn({ keyword, limit, cursor });
    return response.data as AdminMembersResponse;
  } catch (error: any) {
    if (error.code === "functions/permission-denied") {
      throw new Error(error.message ?? "Admin access is required.");
    }
    if (error.code === "functions/unauthenticated") {
      throw new Error(error.message ?? "Please sign in first.");
    }
    throw error;
  }
};

export const getAdminMemberBilling = async (
  userId: string,
  limit = 20,
  cursor: string | null = null
): Promise<BillingTransactionsResponse> => {
  const functions = getFunctions(getApp(), "asia-northeast3");
  const getAdminMemberBillingFn = httpsCallable(
    functions,
    "getAdminMemberBillingOnCall"
  );

  try {
    const response = await getAdminMemberBillingFn({ userId, limit, cursor });
    return response.data as BillingTransactionsResponse;
  } catch (error: any) {
    if (error.code === "functions/permission-denied") {
      throw new Error(error.message ?? "Admin access is required.");
    }
    if (error.code === "functions/unauthenticated") {
      throw new Error(error.message ?? "Please sign in first.");
    }
    throw error;
  }
};

export type AdminDodoRefundResponse = {
  ok: boolean;
  previewOnly?: boolean;
  kind: "full" | "partial";
  refundAmountMinor: number;
  refundAmountUsd: number;
  creditsToRevoke: number;
  usedCredits: number;
  normalUsdPerCrd: number;
  minimumRefundUsd: number;
  dodoRefundId: string | null;
  dodoStatus: string | null;
};

export const requestAdminDodoRefund = async (
  userId: string,
  transactionId: string,
  previewOnly = false
): Promise<AdminDodoRefundResponse> => {
  const functions = getFunctions(getApp(), "asia-northeast3");
  const requestAdminDodoRefundFn = httpsCallable(
    functions,
    "requestAdminDodoRefundOnCall"
  );

  try {
    const response = await requestAdminDodoRefundFn({
      userId,
      transactionId,
      previewOnly,
    });
    return response.data as AdminDodoRefundResponse;
  } catch (error: any) {
    if (error.code === "functions/permission-denied") {
      throw new Error(error.message ?? "Admin access is required.");
    }
    if (error.code === "functions/unauthenticated") {
      throw new Error(error.message ?? "Please sign in first.");
    }
    throw error;
  }
};

type CreateSupportInquiryResponse = {
  supportInquiryId: string;
  ok: boolean;
};

export type SupportChatHistoryItem = {
  role: "user" | "assistant";
  content: string;
};

export type SupportChatResponseType = "normal" | "malicious_repeat";
export type SupportChatViolationType =
  | "none"
  | "out_of_scope"
  | "abuse"
  | "repeat"
  | "spam";

export type SupportChatResponse = {
  answer: string;
  response_type: SupportChatResponseType;
  needs_support_inquiry: boolean;
  violation_type: SupportChatViolationType;
  blocked_until?: string;
};

export type SupportChatBlockStatusResponse = {
  blocked: boolean;
  blockedUntil: string;
  warningCount?: number;
};

export const createSupportInquiry = async (
  userId: string,
  message: string,
  locale: string,
  category: string,
  replyEmail: string
): Promise<CreateSupportInquiryResponse> => {
  const functions = getFunctions(getApp(), "asia-northeast3");
  const createSupportInquiryFn = httpsCallable(
    functions,
    "createSupportInquiryOnCall"
  );

  try {
    const response = await createSupportInquiryFn({
      userId,
      message,
      locale,
      category,
      replyEmail,
    });
    return response.data as CreateSupportInquiryResponse;
  } catch (error: any) {
    if (error.code === "functions/invalid-argument") {
      throw new Error(
        error.message ?? "Please enter at least 10 characters for inquiry."
      );
    }
    if (error.code === "functions/permission-denied") {
      throw new Error(error.message ?? "You can submit only your own inquiry.");
    }
    if (error.code === "functions/unauthenticated") {
      throw new Error(error.message ?? "Please sign in first.");
    }
    throw error;
  }
};

export const requestSupportChatReply = async (
  userId: string,
  message: string,
  locale: string,
  history: SupportChatHistoryItem[]
): Promise<SupportChatResponse> => {
  const functions = getFunctions(getApp(), "asia-northeast3");
  const supportChatFn = httpsCallable(functions, "supportChatOnCall");

  try {
    const response = await supportChatFn({
      userId,
      message,
      locale,
      history,
    });
    const data = response.data as SupportChatResponse;
    return data;
  } catch (error: any) {
    if (error.code === "functions/failed-precondition") {
      throw new Error(
        error.message ??
          "AI support chat is temporarily unavailable. Please try again later."
      );
    }
    if (error.code === "functions/invalid-argument") {
      throw new Error(error.message ?? "Please enter a valid message.");
    }
    if (error.code === "functions/permission-denied") {
      throw new Error(error.message ?? "You can use only your own account.");
    }
    if (error.code === "functions/unauthenticated") {
      throw new Error(error.message ?? "Please sign in first.");
    }
    throw error;
  }
};

export const requestSupportChatBlockStatus = async (
  userId: string
): Promise<SupportChatBlockStatusResponse> => {
  const functions = getFunctions(getApp(), "asia-northeast3");
  const supportChatBlockStatusFn = httpsCallable(
    functions,
    "getSupportChatBlockStatusOnCall"
  );

  try {
    const response = await supportChatBlockStatusFn({ userId });
    const data = response.data as SupportChatBlockStatusResponse;
    return data;
  } catch (error: any) {
    if (error.code === "functions/permission-denied") {
      throw new Error(error.message ?? "You can use only your own account.");
    }
    if (error.code === "functions/unauthenticated") {
      throw new Error(error.message ?? "Please sign in first.");
    }
    throw error;
  }
};

export type BlogKnowledgeItem = {
  id: string;
  title: string;
  description: string;
  tags: string[];
  sourceLabel: string;
  sourceUrl: string;
  materialType: "information" | "media";
  mediaPath: string;
  mediaUrl: string;
  mediaName: string;
  mediaContentType: string;
  streamUid: string;
  streamUrl: string;
  approvedForBlog: boolean;
  publicMedia: boolean;
  createdAt: string;
  updatedAt: string;
};

export type BlogKnowledgeInput = Omit<
  BlogKnowledgeItem,
  "id" | "createdAt" | "updatedAt" | "mediaUrl"
> & { id?: string; imageUrl?: string; mediaUrl?: string };

export type BlogKnowledgeSaveResponse = {
  id: string;
  uploadPath: string;
  mediaPath: string;
  mediaUrl: string;
  mediaName: string;
  mediaContentType: string;
};

export type BlogKnowledgeListResponse = {
  items: BlogKnowledgeItem[];
  hasMore: boolean;
  nextCursor: string | null;
};

export const saveAdminBlogKnowledge = async (
  item: BlogKnowledgeInput
): Promise<BlogKnowledgeSaveResponse> => {
  const functions = getFunctions(getApp(), "asia-northeast3");
  const callable = httpsCallable(functions, "saveAdminBlogKnowledgeOnCall");
  const response = await callable(item);
  return response.data as BlogKnowledgeSaveResponse;
};

export const listAdminBlogKnowledge = async (
  limit = 50,
  cursor: string | null = null
): Promise<BlogKnowledgeListResponse> => {
  const functions = getFunctions(getApp(), "asia-northeast3");
  const callable = httpsCallable(functions, "listAdminBlogKnowledgeOnCall");
  const response = await callable({ limit, cursor });
  return response.data as BlogKnowledgeListResponse;
};

export const deleteAdminBlogKnowledge = async (
  id: string
): Promise<{ ok: boolean; retainedMedia: boolean }> => {
  const functions = getFunctions(getApp(), "asia-northeast3");
  const callable = httpsCallable(functions, "deleteAdminBlogKnowledgeOnCall");
  const response = await callable({ id });
  return response.data as { ok: boolean; retainedMedia: boolean };
};

export type AdminBlogPost = BlogPost & {
  public: boolean;
  previewImageUrl: string;
};

export const listAdminBlogPosts = async (): Promise<AdminBlogPost[]> => {
  const functions = getFunctions(getApp(), "asia-northeast3");
  const callable = httpsCallable(functions, "listAdminBlogPostsOnCall");
  const response = await callable({});
  const data = response.data as { posts?: AdminBlogPost[] };
  return Array.isArray(data.posts) ? data.posts : [];
};

export const setAdminBlogPostPublic = async (
  slug: string,
  isPublic: boolean
): Promise<void> => {
  const functions = getFunctions(getApp(), "asia-northeast3");
  const callable = httpsCallable(functions, "setAdminBlogPostPublicOnCall");
  await callable({ slug, public: isPublic });
};

export const deleteAdminBlogPost = async (slug: string): Promise<void> => {
  const functions = getFunctions(getApp(), "asia-northeast3");
  const callable = httpsCallable(functions, "deleteAdminBlogPostOnCall");
  await callable({ slug });
};

export const createAdminCloudflareStreamUpload = async (
  fileName: string,
  contentType: string,
  size: number
): Promise<{ uid: string; uploadURL: string; streamUrl: string }> => {
  const functions = getFunctions(getApp(), "asia-northeast3");
  const callable = httpsCallable(
    functions,
    "createAdminCloudflareStreamUploadOnCall"
  );
  const response = await callable({ fileName, contentType, size });
  return response.data as {
    uid: string;
    uploadURL: string;
    streamUrl: string;
  };
};

export const createAdminCloudflareR2Upload = async (
  itemId: string,
  fileName: string,
  contentType: string,
  size: number
): Promise<{ objectKey: string; uploadURL: string }> => {
  const functions = getFunctions(getApp(), "asia-northeast3");
  const callable = httpsCallable(
    functions,
    "createAdminCloudflareR2UploadOnCall"
  );
  const response = await callable({ itemId, fileName, contentType, size });
  return response.data as { objectKey: string; uploadURL: string };
};
